import React, { useState, useContext } from "react";
import {
  Box,
  Text,
  Flex,
  FormControl,
  Button,
  Input,
  ButtonGroup,
  InputRightElement,
  InputGroup,
  IconButton,
  Spinner,
  Container
} from "@chakra-ui/react";
import { yupResolver } from "@hookform/resolvers/yup";
import { useForm } from "react-hook-form";

import { Link } from "react-router-dom";
import { t } from "@lang/i18n";
import { AuthContext } from "@contexts/AuthContext";
import { LOGGED_IN } from "@constants";
import Layout from "./Layout";
import { Logo, IconEye } from "@assets/Icons";
import { login, sendActivationEmail } from "@lib/datahandlers/auth";
import { Separator, ErrorBox, View, ModalBox,  } from "@components";
import { loginSchema } from "@lib/validationData";
import { showPasswordHandler } from "@lib/functions";

const LoginScreen = () => {
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setIsLoading] = useState(false);
  const [showModalBox, setShowModalBox] = useState({ value: false, code: "" });

  const { dispatch, toast } = useContext(AuthContext);
  const { register, handleSubmit, getValues, formState: { errors } } = useForm({
    resolver: yupResolver(loginSchema)
  });

  const handleShowClick = () => (setShowPassword(!showPassword));

  const handleClick = () => {
    if (showModalBox.code === "EMAIL_NOT_CONFIRMED") {
      setShowModalBox({value: false, code: ""});
      onResendActivationEmail(getValues('email'));
    }
    setShowModalBox({value: false, code: ""});                            
  };

  const onLogin = async (data) => {
    setIsLoading(true);
    const result = await login(data);

      if (result.success) {
        setIsLoading(false);
        dispatch({
          type: LOGGED_IN,
          data: result.user,
          token_access: result.token_access,
        });
        return;
      } 
      setIsLoading(false);
      if (result.errorCode === "EMAIL_NOT_CONFIRMED" || result.errorCode === "ACCOUNT_PENDING_REVIEW") {
        setShowModalBox({ value: true, code: result.errorCode });
        return;
      }
      toast({ description: t("messages.error.login") , status: "error", position: "bottom-right", variant: "left-accent", isClosable: true });
  };

  const onResendActivationEmail = async (data) => {
    if (typeof data === 'undefined') return;

    const result = await sendActivationEmail({email: data, user_type: "company"});

    if (result.success) {
      setShowModalBox({value: false});
      toast({ description: result.data.message , status: "success", position: "bottom-right", variant: "left-accent", isClosable: true });
      return;
    }
    toast({ description: t("messages.error.resend_activation_email") , status: "error", position: "bottom-right", variant: "left-accent", isClosable: true });
  };

  return (
    <Layout isFooterVisible>
      <View if={showModalBox.value}>
          <ModalBox isOpen={showModalBox.value} >
              <Text color="text.bold" textAlign="center" fontSize="16px" fontWeight="500">
                  { showModalBox.code === "ACCOUNT_PENDING_REVIEW" ? t('company.register.account_pending_review') : t('company.register.inactive_account') }
              </Text>

              <ButtonGroup spacing="6" mt="30px">
                  <Button bg="error" borderRadius="24px" color="white" w="164px" h="40px"
                      _hover={{ opacity: ".85" }}
                      onClick={() => setShowModalBox({value: false})}>
                      { t('global.close') }
                  </Button>
                  { showModalBox.code === "EMAIL_NOT_CONFIRMED" &&
                      <Button bg="gradient" borderRadius="24px" color="white" w="164px" h="40px"
                          _hover={{ opacity: ".85" }}
                          onClick={() => handleClick()}>
                          { t('global.ok') }
                      </Button> }
              </ButtonGroup>
          </ModalBox>
      </View>

      <Container maxW="429px" bg="white" py="39px" px={["16px","","","42px"]} borderRadius="4px"  
        m={["0px auto 129px auto", "", "63px auto 129px auto"]} backdropBlur="blur(10px)" opacity=".75">
        <Flex flexDir="column"> 
          <Box mx="auto" mb="16px">
            <Logo logoType="auth" />
          </Box>

          <Box as="span" fontWeight="400" fontSize="16px" textAlign="center">
            {t("auth.login.title")}
          </Box>

          <Flex flexDir="row" flexWrap="wrap" justifyContent="center" fontSize="35px" fontWeight="800" mx="auto" w="345px">
            <Box display="flex" justifyContent="space-around">
              <Box  as="span" color="blue.dark" fontWeight="800" >
                {t("auth.login.find")}
              </Box>
              <Box as="span" m ="0 1rem" color="black" fontWeight="400">
                /
              </Box>
              <Box  as="span" color="green.dark" fontWeight="800">
                {t("auth.login.offer")}
              </Box>
            </Box>
            <Box as="span" color="black" fontWeight="400">
              {t("auth.login.a_job")}
            </Box>
          </Flex>

          <Separator />

          <form onSubmit={handleSubmit(onLogin)}>
            <Flex flexDir="column" alignItems="center" justifyContent="center"> 
              <FormControl id="email" mb="16px" maxW="345px">
                <Input
                  type="email"
                  placeholder={t("fields.email")}
                  isInvalid={errors.email ? true : false}
                  errorBorderColor="error"
                  borderColor="gray.borders"
                  borderRadius="4px" bg="white" mb="2px" maxW="345px" h="46px"
                  {...register("email")}
                />
                {errors.email && <ErrorBox error={errors.email.message} />}
              </FormControl>

              <FormControl id="password" maxW="345px">
                <InputGroup justifyContent="center">
                  <Input
                    type={showPassword ? "text" : "password"}
                    placeholder={t("fields.password")}
                    isInvalid={errors.password ? true : false}
                    errorBorderColor="error"
                    maxW="345px" h="46px"
                    borderColor="gray.borders" borderRadius="4px" bg="white" mt="10px"
                    pos="relative"
                    {...register("password")}
                  />
                  <InputRightElement>
                    <IconButton
                      onClick={handleShowClick}
                      icon={<IconEye opened={showPasswordHandler(showPassword)} />}
                      bg="invisible"
                      pos="absolute"
                      top="32%"
                      right="15%"
                      _hover={{ bg: "invisible" }}
                      _focus={{ outline: "none" }}
                      _active={{ bg: "white" }}
                    />
                  </InputRightElement>
                </InputGroup>
                {errors.password && <ErrorBox error={errors.password.message} />}
              </FormControl>
            </Flex>

            <Flex flexDir="row" flexWrap="wrap" alignItems="center" justifyContent="center" mt="16px">
              <Link to="/forgot-password">
                <Box p="0 .25rem" fontWeight="600" fontSize="14px" textDecor="underline">
                  {t("auth.login.forgot_password")}
                </Box>
              </Link>

              <Button type="submit" w="152px" h="40px" borderRadius="24px" bg="gradient" color="white" transitionDuration=".2s"
                _hover={{ filter: "saturate(1.5)"}} _active={{ filter: "saturate(1.5)", top: "1px"}} m="14px">
                {loading? <Spinner size="md" /> : t("auth.login.login")}
              </Button>
            </Flex>
          </form>

          <Text fontWeight="400" color="black" fontSize="16px" mt="48px" mb="21px" textAlign="center">
            {t("auth.login.no_account")}
          </Text>

          <ButtonGroup flexDir="column" alignItems="center" spacing="0">
            <Link to="/candidate/register">
              <Button w={["80vw",  "21rem", "", ""]} p=".5rem 1rem" borderRadius="24px" mb="16px" bg="white" border="2px solid" borderColor="blue.main" color="blue.main" 
                      _hover={{ borderColor: "blue.light", color: "blue.light" }}>
                {t("auth.login.create_person_account")}
              </Button>
            </Link>

            <Link to="/company/register">
              <Button w={["80vw",  "21rem", "", ""]} p=".5rem 1rem" borderRadius="24px" bg="white" border="2px solid" borderColor="green.main" color="green.main" 
                      _hover={{ borderColor: "green.light", color: "green.light" }}>
                {t("auth.login.create_company_account")}
              </Button>
            </Link>
          </ButtonGroup>
        </Flex>
      </Container>
    </Layout>
  );
};

export default LoginScreen;
